import { useNavigate } from 'react-router-dom';
import { MessageSquare, Plus, MessageCircle } from 'lucide-react';
import { useTickets } from '../api/hooks';
import { Cell, Chip, EmptyState, PageHeader, Section, Skeleton } from '../components/ui';
import { useT, useLocale } from '../i18n';

export function Tickets() {
  const navigate = useNavigate();
  const t = useT();
  const locale = useLocale();
  const tickets = useTickets();
  const items = extractList(tickets.data);

  const open = items.filter((tk) => isOpen(extractStatusCode(tk.status)));
  const closed = items.filter((tk) => !isOpen(extractStatusCode(tk.status)));

  return (
    <>
      <PageHeader
        title={t('tickets.title')}
        subtitle={t('tickets.countSuffix', { n: items.length })}
        right={
          <button
            onClick={() => navigate('/tickets/new')}
            style={{
              color: 'var(--tg-button)',
              fontSize: 14,
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <Plus size={16} />
            {t('tickets.new')}
          </button>
        }
      />

      {tickets.isLoading && (
        <Section>
          <div style={{ padding: 14, display: 'grid', gap: 10 }}>
            <Skeleton height={48} radius={12} />
            <Skeleton height={48} radius={12} />
          </div>
        </Section>
      )}

      {!tickets.isLoading && items.length === 0 && (
        <Section>
          <EmptyState
            icon={<MessageSquare size={28} />}
            title={t('tickets.empty.title')}
            description={t('tickets.empty.desc')}
          />
        </Section>
      )}

      {open.length > 0 && (
        <Section title={t('tickets.groupOpen')}>
          {open.map((tk, idx) => (
            <TicketRow
              key={String(tk.ticket_id ?? tk.id ?? idx)}
              t={t}
              locale={locale}
              ticket={tk}
              last={idx === open.length - 1}
              onClick={() => navigate(`/tickets/${tk.ticket_id ?? tk.uuid ?? tk.id ?? ''}`)}
            />
          ))}
        </Section>
      )}

      {closed.length > 0 && (
        <Section title={t('tickets.groupClosed')}>
          {closed.map((tk, idx) => (
            <TicketRow
              key={String(tk.ticket_id ?? tk.id ?? idx)}
              t={t}
              locale={locale}
              ticket={tk}
              last={idx === closed.length - 1}
              onClick={() => navigate(`/tickets/${tk.ticket_id ?? tk.uuid ?? tk.id ?? ''}`)}
            />
          ))}
        </Section>
      )}
    </>
  );
}

function TicketRow({
  t,
  locale,
  ticket,
  last,
  onClick,
}: {
  t: ReturnType<typeof useT>;
  locale: string;
  ticket: Record<string, unknown>;
  last: boolean;
  onClick?: () => void;
}) {
  const title = String(ticket.subject ?? ticket.title ?? t('ticket.detail.fallbackTitle'));
  const statusObj = ticket.status as Record<string, unknown> | string | undefined;
  const statusCode =
    typeof statusObj === 'string'
      ? statusObj
      : statusObj && typeof statusObj === 'object'
        ? String(statusObj.code ?? '')
        : '';
  const statusDisplay =
    typeof statusObj === 'object' && statusObj && typeof statusObj.display === 'string'
      ? statusObj.display
      : statusLabel(statusCode, t);
  const departmentDisplay = (() => {
    if (typeof ticket.department !== 'object' || !ticket.department) return '';
    const d = ticket.department as Record<string, unknown>;
    const display = typeof d.display === 'string' ? d.display : '';
    const code = typeof d.code === 'string' ? d.code : '';
    // Display from API; fallback to translated code; fallback to raw code.
    if (display) return display;
    if (code) {
      const key = `ticket.dept.${code}` as Parameters<typeof t>[0];
      const out = t(key);
      return out === key ? code : out;
    }
    return '';
  })();
  const updatedAt = String(ticket.updated_at ?? ticket.created_at ?? '');
  const hasUnread = !!ticket.has_unread;
  const subtitle = [departmentDisplay, fmtRelative(updatedAt, locale, t)]
    .filter(Boolean)
    .join(' · ');
  return (
    <Cell
      onClick={onClick}
      last={last}
      leading={
        <div
          style={{
            position: 'relative',
            width: 38,
            height: 38,
            borderRadius: 12,
            background: hasUnread ? 'rgba(7,141,238,0.10)' : 'var(--tg-secondary-bg)',
            color: hasUnread ? 'var(--brand)' : 'var(--g-600)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MessageCircle size={18} />
          {hasUnread && (
            <span
              style={{
                position: 'absolute',
                top: -2,
                right: -2,
                width: 8,
                height: 8,
                borderRadius: 4,
                background: 'var(--danger)',
                border: '2px solid var(--tg-section-bg)',
              }}
            />
          )}
        </div>
      }
      title={<span style={{ fontWeight: hasUnread ? 700 : 500 }}>{title}</span>}
      subtitle={subtitle}
      trailing={<Chip color={mapStatusToColor(statusCode)}>{statusDisplay}</Chip>}
    />
  );
}

function mapStatusToColor(code: string): 'success' | 'warning' | 'neutral' | 'danger' {
  const lo = code.toLowerCase();
  if (lo.includes('open') || lo.includes('pending') || lo.includes('answered'))
    return lo.includes('answered') ? 'success' : 'warning';
  if (lo.includes('closed') || lo.includes('resolved')) return 'neutral';
  return 'neutral';
}

function fmtRelative(v: string, locale: string, t: ReturnType<typeof useT>): string {
  if (!v) return '';
  const d = new Date(v.replace(/-/g, '/'));
  if (Number.isNaN(d.getTime())) return v;
  const diff = Date.now() - d.getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return t('common.justNow');
  if (s < 3600) return t('common.minutesAgo', { n: Math.floor(s / 60) });
  if (s < 86400) return t('common.hoursAgo', { n: Math.floor(s / 3600) });
  if (s < 86400 * 30) return t('common.daysAgo', { n: Math.floor(s / 86400) });
  return d.toLocaleDateString(locale === 'zh' ? 'zh-CN' : 'en-US', { month: 'short', day: 'numeric' });
}

function isOpen(s: string): boolean {
  const lo = s.toLowerCase();
  return !['closed', 'resolved', 'done'].some((x) => lo.includes(x));
}

function statusLabel(s: string, t: ReturnType<typeof useT>): string {
  const lo = s.toLowerCase();
  if (lo.includes('open') || lo.includes('pending')) return t('tickets.statusPending');
  if (lo.includes('reply') || lo.includes('answered')) return t('tickets.statusReplied');
  if (lo.includes('closed') || lo.includes('resolved')) return t('tickets.statusClosed');
  return s || t('common.dash');
}

function extractList(data: unknown): Array<Record<string, unknown>> {
  if (!data || typeof data !== 'object') return [];
  const obj = data as Record<string, unknown>;
  const inner = (obj.data as Record<string, unknown> | undefined) ?? obj;
  if (Array.isArray(inner)) return inner as Array<Record<string, unknown>>;
  const list =
    (inner as Record<string, unknown>).tickets ??
    (inner as Record<string, unknown>).list ??
    (inner as Record<string, unknown>).items ??
    inner;
  if (Array.isArray(list)) return list as Array<Record<string, unknown>>;
  return [];
}

function extractStatusCode(s: unknown): string {
  if (typeof s === 'string') return s;
  if (s && typeof s === 'object') {
    const o = s as Record<string, unknown>;
    return typeof o.code === 'string' ? o.code : '';
  }
  return '';
}
